import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { IconComponent } from '../icon/icon.component';
import { IconName } from '../icon/icons';

export type ProtectionLevel = 'base' | 'good' | 'high';

/** Testo, icona e colori di ciascun livello: il livello porta sempre la parola. */
export const PROTECTION_LEVEL_STYLES: Record<
  ProtectionLevel,
  { label: string; icon: IconName; classes: string }
> = {
  base: {
    label: 'Protezione base',
    icon: 'shield',
    classes: 'bg-bsite-100 text-bsite-700 dark:bg-bsite-950 dark:text-bsite-200',
  },
  good: {
    label: 'Protezione buona',
    icon: 'shield-check',
    classes: 'bg-stone-100 text-stone-700 dark:bg-stone-800 dark:text-stone-300',
  },
  high: {
    label: 'Protezione alta',
    icon: 'shield-check',
    classes: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300',
  },
};

/**
 * Livello di protezione dell'accesso (Base, Buona, Alta) come etichetta a pillola con lo scudo,
 * con un suggerimento facoltativo accanto, per esempio "Aggiungi una passkey".
 */
@Component({
  selector: 'bs-protection-level',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IconComponent],
  host: { class: 'inline-flex' },
  template: `
    <div
      class="inline-flex flex-wrap items-center gap-x-3 gap-y-1"
      [attr.data-testid]="testId()"
      [attr.data-level]="level()"
    >
      <span [class]="classes()">
        <bs-icon [name]="style().icon" [size]="16" />
        {{ style().label }}
      </span>
      @if (hint(); as text) {
        <span class="text-sm text-stone-600 dark:text-stone-300">{{ text }}</span>
      }
    </div>
  `,
})
export class ProtectionLevelComponent {
  readonly level = input.required<ProtectionLevel>();
  readonly hint = input<string | null>(null);
  readonly testId = input<string | null>(null);

  protected readonly style = computed(() => PROTECTION_LEVEL_STYLES[this.level()]);
  protected readonly classes = computed(
    () =>
      `inline-flex h-7 items-center gap-1.5 whitespace-nowrap rounded-full px-3 text-sm font-semibold ring-1 ring-inset ring-current/20 ${this.style().classes}`,
  );
}
